import {isURLDangerous} from './isURLDangerous';

const SCHEME_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
// Mirrors RTK Query fetchBaseQuery's isAbsoluteUrl. joinUrls drops the API base URL for anything
// matching this, so a mid-path "x://evil" is dangerous too, not only an anchored leading scheme.
const ABSOLUTE_URL_REGEX = /(^|:)\/\//;
const MAX_CONTROL_CHAR_CODE = 0x1f;
const DELETE_CHAR_CODE = 0x7f;
const MAX_DECODE_PASSES = 3;

function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= MAX_CONTROL_CHAR_CODE || code === DELETE_CHAR_CODE) {
      return true;
    }
  }

  return false;
}

/**
 * Validates a relative API path dynamically supplied by the backend (e.g. checkout
 * notification action buttons) before it is passed to the API base query.
 *
 * Unlike isSafeUrl (a navigation-safety validator), this accepts bare relative paths
 * without a leading slash such as "checkout/v1/checkout/{id}/payment/{id}", because the
 * API base query resolves them against the configured API origin, not window.origin.
 * It still rejects anything that could redirect the authenticated request to another
 * origin: scheme/absolute URLs (https:, javascript:), protocol-relative URLs, backslash
 * confusion, control characters, and parent-directory traversal — on both the raw and
 * decoded forms.
 */
export function isSafeRelativeApiPath(path: string): boolean {
  if (!path || typeof path !== 'string') {
    return false;
  }

  if (path !== path.trim()) {
    return false;
  }

  if (hasControlCharacter(path) || path.includes('\\')) {
    return false;
  }

  let inspected = path;
  for (let i = 0; i < MAX_DECODE_PASSES; i++) {
    if (!inspected.includes('%')) {
      break;
    }
    try {
      const next = decodeURIComponent(inspected);
      if (next === inspected) {
        break;
      }
      inspected = next;
    } catch {
      return false;
    }
  }

  if (inspected !== inspected.trim() || inspected.includes('\\')) {
    return false;
  }

  if (hasControlCharacter(inspected)) {
    return false;
  }

  if (isURLDangerous(path) || isURLDangerous(inspected)) {
    return false;
  }

  // Reject anything that could point at another origin (a scheme or a protocol-relative
  // authority) on either the raw or the decoded form.
  if (
    SCHEME_REGEX.test(path) ||
    SCHEME_REGEX.test(inspected) ||
    ABSOLUTE_URL_REGEX.test(path) ||
    ABSOLUTE_URL_REGEX.test(inspected)
  ) {
    return false;
  }

  // Reject parent-directory traversal on either form.
  if (path.split('/').includes('..') || inspected.split('/').includes('..')) {
    return false;
  }

  return true;
}
